import React, { forwardRef, useState } from "react";
import styles from "./Input.module.css";

const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder("utf-8", { fatal: false });

function utf8ByteLength(str: string): number {
  return utf8Encoder.encode(str).length;
}

/** 按 UTF-8 字节截断，避免截断多字节字符 */
function truncateUtf8ByBytes(str: string, maxBytes: number): string {
  const enc = utf8Encoder.encode(str || "");
  if (enc.length <= maxBytes) return str || "";
  let end = maxBytes;
  while (end > 0 && (enc[end]! & 0xc0) === 0x80) end--;
  return utf8Decoder.decode(enc.subarray(0, end));
}

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "prefix" | "suffix"
> {
  className?: string;
  error?: boolean;
  /** 输入框左侧插槽 */
  prefix?: React.ReactNode;
  /** 输入框右侧插槽 */
  suffix?: React.ReactNode;
  /**
   * maxLength 计量方式：
   * - char：按字符计数与展示（默认）
   * - utf8-bytes：按 UTF-8 字节截断；不展示计数，超限时触发 onMaxLengthReached
   */
  maxLengthMode?: "char" | "utf8-bytes";
  /** 输入因 maxLength 被截断时回调（如 toast 提示） */
  onMaxLengthReached?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = "",
      error,
      style,
      prefix,
      suffix,
      maxLengthMode = "char",
      onMaxLengthReached,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const {
      maxLength,
      value,
      onFocus,
      onBlur,
      onChange,
      disabled,
      defaultValue,
      style: inputStyleFromProps,
      ...inputRest
    } = props as React.InputHTMLAttributes<HTMLInputElement>;

    // Handle uncontrolled component state for character count
    const [uncontrolledValue, setUncontrolledValue] = useState(
      defaultValue ?? ""
    );
    const isControlled = value !== undefined;
    const currentVal = isControlled ? value : uncontrolledValue;
    const valStr =
      currentVal === null || currentVal === undefined ? "" : String(currentVal);
    const currentLen = valStr.length;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let next = e.target.value;
      let truncated = false;
      if (
        typeof maxLength === "number" &&
        maxLengthMode === "utf8-bytes" &&
        utf8ByteLength(next) > maxLength
      ) {
        next = truncateUtf8ByBytes(next, maxLength);
        truncated = true;
      }
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      if (truncated) {
        onMaxLengthReached?.();
      }
      if (next === e.target.value) {
        onChange?.(e);
        return;
      }
      const target = Object.assign(e.target, { value: next });
      onChange?.({
        ...e,
        target,
        currentTarget: target,
      });
    };

    // utf8-bytes：不展示计数（改由业务 toast 提示上限）
    const showCount =
      focused && maxLength !== undefined && maxLengthMode !== "utf8-bytes";
    const hasPrefix = prefix != null && prefix !== false;
    const hasSuffix = suffix != null && suffix !== false;

    const userInputStyle =
      inputStyleFromProps &&
      typeof inputStyleFromProps === "object" &&
      !Array.isArray(inputStyleFromProps)
        ? (inputStyleFromProps as React.CSSProperties)
        : undefined;

    return (
      <div
        data-ui-workshop-slot="input"
        className={`${styles.wrapper} ${error ? styles.error : ""} ${
          disabled ? styles.disabled : ""
        } ${className}`}
        style={style}
      >
        {hasPrefix && <div className={styles.affixPrefix}>{prefix}</div>}
        <div className={styles.inputWrap}>
          <input
            ref={ref}
            className={styles.input}
            {...inputRest}
            {...(isControlled ? { value: valStr } : { defaultValue })}
            disabled={disabled}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{
              ...userInputStyle,
              paddingRight: showCount ? "60px" : "",
            }}
          />
          {showCount && (
            <span className={styles.count}>
              {currentLen}/{maxLength}
            </span>
          )}
        </div>
        {hasSuffix && <div className={styles.affixSuffix}>{suffix}</div>}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
